import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { SearchResult } from '../../../../../models/search-result';
import { HomeworkLink } from '../../../../../models/homework-link';
import { HomeworkService } from '../../../../../services/homework.service';
import { KeyValues } from '../../../../../utils/key-values';
import { QuickLinkService } from '../../../../../services/quick-link.service';
import { PageService } from '../../../../../services/page.service';
import { environment } from '../../../../../../environments/environment';
import { ApplicationStateService } from '../../../../../services/application-state.service';
import { CommunicationService } from '../../../../../services/communication.service';
import { AssetService } from '../../../../../services/asset.service';
import { Page } from '../../../../../models/page';
import { QuickLink } from '../../../../../models/quick-link';
import { Subscription } from 'rxjs';
import { ModalService } from '../../../../../services/modal.service';
import { HomeworkFactory } from '../../../../../models/factories/homework-factory';
import _ from 'lodash';
import { RestService } from '../../../../../services/rest.service';
import { RouteService } from '../../../../../services/route.service';
import { QuickLinkSearchResult } from '../../../../../models/quick-link-search-result';
import { SchoolClassService } from '../../../../../services/school-class.service';
import { HsmLinkSearchResult } from '../../../../../models/hsm-link-search-result';
import { Assignment } from '../../../../../models/assignment';
import { QuickLinkUtils } from '../../../../../utils/quick-link-utils';
import { BookWrapper } from '../../../../../models/book-wrapper';
import { AlertService } from '../../../../../services/alert.service';
import { AssetPipe } from '../../../../../pipes/asset.pipe';
import { EllipsisPipe } from '../../../../../pipes/ellipsis.pipe';
import { FormsModule } from '@angular/forms';
import { CrudService } from '../../../../../services/crud.service';
import { TechnicalFeature } from '@lemberger/lemberger-am';

@Component({
    selector: ' test-generator-form',
    templateUrl: './test-generator-form.component.html',
    styleUrls: ['./test-generator-form.component.scss'],
    standalone: true,
    imports: [
        AssetPipe,
        EllipsisPipe,
        FormsModule
    ]
})
export class TestGeneratorFormComponent implements OnInit, OnDestroy {
    private crudService = inject(CrudService);

    hsmLinks: Array<HsmLinkSearchResult>;
    timer = null;
    focusDropdown = false;

    title: string;
    date: Date | string | number;
    isValidDate = true;
    email: string;
    validEmail: boolean;
    exercise: string;
    prefix: string;
    cooldownPhase = 1440;

    cooldownPhaseOptions = [
        {
            value: 5,
            label: '5 Minuten'
        },
        {
            value: 15,
            label: '15 Minuten'
        },
        {
            value: 60,
            label: '1 Stunde'
        },
        {
            value: 240,
            label: '4 Stunden'
        },
        {
            value: 1440,
            label: '1 Tag'
        },
        {
            value: 10080,
            label: '1 Woche'
        }
    ];

    schoolClass = 0;
    notifySchoolClass = 0;
    notifySchoolClassText: string;

    dragSrcEl: string;

    chooserOpened: boolean;
    quickLinks: Array<QuickLink>;
    page: Page;
    pageNumber: string;
    pages: Array<Page>;

    homeworkAddedSubject: Subscription;
    homeworkChangeSubject: Subscription;
    addHomeworkSubscription: Subscription;

    errorMessages = [];
    loading = false;
    debouncedValidateEmail: (() => void) & _.Cancelable;

    selectedBook: BookWrapper;
    bookData: Array<BookWrapper>;

    recommendations = [];
    editHomework: HomeworkLink;
    isEdited = false;
    private debounceQuery: (() => void) & _.Cancelable;

    constructor(public applicationStateService: ApplicationStateService,
                public assetService: AssetService,
                public homeworkService: HomeworkService,
                private communicationService: CommunicationService,
                private quickLinkService: QuickLinkService,
                private pageService: PageService,
                private alertService: AlertService,
                private modalService: ModalService,
                private restService: RestService,
                private routeService: RouteService,
                public schoolClassService: SchoolClassService) {
        this.hsmLinks = [];
        this.debouncedValidateEmail = _.debounce(this.validateEmail, 100);
        this.debounceQuery = _.debounce(this.searchQuery, 300);

        this.homeworkChangeSubject = this.communicationService.homeworkChangeAnnounced$.subscribe(() => {
            this.isEdited = true;
            this.fillHomework(this.homeworkService.getActualHomework());
        });

        this.homeworkAddedSubject = this.communicationService.addHomeworkAnnounced$.subscribe(() => {
            this.isEdited = true;
            this.fillHomework(this.homeworkService.getActualHomework());
        });
    }

    ngOnInit(): void {
        this.homeworkService.formAddedTests = [];

        this.fillHomework(this.homeworkService.getActualHomework());

        const route = this.routeService.getRelativeRoute(environment.endpointUrls.hsmLinksBooks, {});
        const currentBookOption = Object.assign(new BookWrapper(),
            {
                id: this.applicationStateService.bookId,
                coverPath: this.pageService.getPageByIndex(0).thumbnailImage.url,
                title: 'Beispiele aus <strong>diesem Buch</strong>',
                prefix: this.applicationStateService.bookConfig.quickLinkPrefix
            }
        );
        this.bookData = [currentBookOption];

        this.selectedBook = currentBookOption;

        this.restService.get(route).then(result => {
            this.bookData = [...this.bookData, ...this.filterBooks(result)];
        });

        if (this.applicationStateService.bookConfig.quickLinkPrefix) {
            this.prefix = this.applicationStateService.bookConfig.quickLinkPrefix.toLowerCase();
        }
        this.pages = this.pageService.getAllPages();

        if (this.homeworkService.testGeneratorOpened) {
            this.homeworkService.activateHomework();
        }

        this.initFields();

        this.addHomeworkSubscription = this.homeworkService.addHomeworkExercise$.subscribe((value: any) => {
            this.addResult(value.result);
        });
    }

    ngOnDestroy(): void {
        this.communicationService.clearHomework();
        this.loading = false;
        this.homeworkAddedSubject.unsubscribe();
        this.homeworkChangeSubject.unsubscribe();
        this.addHomeworkSubscription.unsubscribe();
    }

    editMode() {
        return this.homeworkService.testGeneratorOpened;
    }

    /**
     * is user a teacher
     *
     * @returns {boolean}
     */
    isTeacher(): boolean {
        return this.homeworkService.isTeacher();
    }

    /**
     * add exercise to homework
     */
    onPlus(): void {
        this.clearErrors();
        let result = _.flatten(
            this.hsmLinks.map(ele => ele.hsmLinks.map(ql => {
                const text = ql.text;

                return { text, pageId: ql.page_id } as SearchResult;
            }))).find(value => {

            if (!value.text) {
                return false;
            }

            this.communicationService.announceHomeworkChange();

            return value.text.toUpperCase() === this.exercise.toUpperCase();
        });

        if (!result && this.exercise) {
            result = new SearchResult();
            result.text = this.exercise.toUpperCase();
        }
        if (result) {
            this.communicationService.announceHomeworkChange();
        }

        if (result.text) {
            this.addResult(result.text);
        }
    }

    /**
     * remove exercise from homework
     *
     * @param {SearchResult} link
     */
    onMinus(link: SearchResult): void {
        this.clearErrors();
        this.homeworkService.formAddedTests = this.homeworkService.formAddedTests.filter(value => {
            if (typeof value !== 'string') {
                return value.text.toUpperCase() !== link.text.toUpperCase();
            }

            return true;
        });
        this.syncHomework();
        this.communicationService.announceHomeworkChange();
    }

    /**
     * turn to page of search result
     *
     * @param link
     */
    openQuicklink(link: SearchResult): void {
        const quickLink = new QuickLink();
        quickLink.slug = link.text;
        // Make it absolute route so links from other books can also be opened
        quickLink.link = `${this.applicationStateService.domain}/hsm/${link.text}`;
        quickLink.bypassCheck = this.applicationStateService.bookConfig.bookPermissions.homework.use;

        this.modalService.openModal(quickLink);
    }

    /**
     * Call api to get search results for search text / query
     *
     * @return void
     */
    searchQuery(): void {
        this.hsmLinks = [];
        // const currPrefix = this.selectedBook.prefix || '';
        // const queryWithoutPrefix = this.exercise.replace(currPrefix, '');

        // if (!this.exercise || queryWithoutPrefix.length < 1 || currPrefix.includes(this.exercise)) {
        if (!this.exercise || this.exercise.length < 1) {
            return;
        }

        this.fetchRegularQuicklinks(); // only regular quicklinks and no am-links are displayed in the dropdown list.
    }

    /**
     * event handler (for keyboard timeout)
     *
     * @param event
     */
    keyUpExercise(event): void {
        this.clearErrors();
        if (this.exercise !== '') {
            this.searchQuery();
            this.focusDropdown = true;

            return;
        }

        this.focusDropdown = true;
        this.debounceQuery();
    }

    /**
     * @param {SearchResult} result
     */
    addResult(result: string): void {
        const url = this.routeService.getRelativeRoute(environment.endpointUrls.quicklink, {
            quicklink: result
        });

        this.restService.get(url).then((value: Assignment) => {
            if (QuickLinkUtils.isHSMLinkPattern(value.meta.quicklink)) {
                this.addAmLinks(value);
            } else {
                this.addQuicklink(value.meta.quicklink, value);
            }

            this.exercise = undefined;
        }).catch(err => {
            this.errorMessages.push('Ein unbekannter Fehler ist aufgetreten.');
            console.error(err);
        });
    }

    /**
     * fill form inputs with given homework infos
     *
     * @param {HomeworkLink} hw
     */
    fillHomework(hw: HomeworkLink): void {
        this.homeworkService.formAddedTests = hw.exercises;
    }

    /**
     * called when exercise text field looses focus, to deactivate auto complete
     */
    focusOut(): void {
        setTimeout(() => {
            this.focusDropdown = false;
        }, 300);
    }

    /**
     * close homework editor / deactivate edit mode
     */
    async close(): Promise<void> {
        if (this.homeworkService.formAddedTests.length && this.editMode() && this.isEdited) {
            if (!await this.alertService.alert(AlertService.TASKS_CONFIRMATION)) {
                return;
            }
        }
        this.communicationService.clearHomework();
    }

    async toggle(): Promise<void> {
        if (!this.applicationStateService.userData.bookAccessCode) {
            this.modalService.openDisabledTestGeneratorModal();

            return;
        }
        if (this.homeworkService.formAddedTests.length && this.editMode() && this.isEdited) {
            if (!await this.alertService.alert(AlertService.TASKS_CONFIRMATION)) {
                return;
            }
        }

        this.editHomework = null;
        this.homeworkService.formAddedTests = [];
        this.syncHomework();
        this.communicationService.announceHomeworkChange();
        this.homeworkService.testGeneratorOpened = !this.homeworkService.testGeneratorOpened;
    }

    /**
     * handle drag start event
     *
     * @param $e
     * @param {SearchResult} link
     */
    handleDragStart($e, link: SearchResult): void {
        // Target (this) element is the source node

        this.dragSrcEl = link.text;

        $e.dataTransfer.effectAllowed = 'move';
        $e.dataTransfer.setData('text/plain', link.text);
    }

    /**
     * handle drag over event
     *
     * @param $e
     * @return {boolean}
     */
    handleDragOver($e): boolean {
        if ($e.preventDefault) {
            $e.preventDefault(); // Necessary. Allows us to drop.
        }

        $e.dataTransfer.dropEffect = 'move';  // See the section on the DataTransfer object.

        return false;
    }

    /**
     * handle drag enter event
     *
     * @param $e
     */
    handleDragEnter($e): void {
        // this / e.target is the current hover target.
        $e.target.classList.add('over');
    }

    /**
     * handle drag leave event
     *
     * @param $e
     */
    handleDragLeave($e): void {
        $e.target.classList.remove('over');  // this / e.target is previous target element.
    }

    /**
     * handle drop event
     *
     * @param $e
     * @param {SearchResult} link
     */
    handleDrop($e, link: SearchResult): boolean {
        // this / e.target is current target element.

        if ($e.stopPropagation) {
            $e.stopPropagation(); // stops the browser from redirecting.
        }

        // See the section on the DataTransfer object.
        // Don't do anything if dropping the same column we're dragging.
        if (this.dragSrcEl !== link.text) {
            // Set the source column's HTML to the HTML of the columnwe dropped on.
            const data = $e.dataTransfer.getData('text/plain');
            this.moveBefore(data, link);
        }

        return false;
    }

    /**
     * handle drag end event
     *
     * @param $e
     */
    handleDragEnd($e): void {
        // this/e.target is the source node.

        const cols = $('.exercise-list li');

        [].forEach.call(cols, col => {
            col.classList.remove('over');
        });
    }

    /**
     * move exercise with given moveText before the given exercise element
     *
     * @param {string} moveText
     * @param {SearchResult} element
     */
    moveBefore(moveText: string, element: SearchResult): void {
        const moveElem = this.homeworkService.formAddedTests.find(value => {
            return value.text === moveText;
        });
        if (!moveElem) {
            return;
        }

        this.homeworkService.formAddedTests = this.homeworkService.formAddedTests.filter(value => {
            return value.text !== moveText;
        });

        const newList = [];
        let lastElem: SearchResult;
        this.homeworkService.formAddedTests.forEach(value => {
            if (lastElem) {
                newList.push(lastElem);
                lastElem = value;
            } else if (value === element) {
                lastElem = element;
                newList.push(moveElem);
            } else {
                newList.push(value);
            }
        });
        if (lastElem) {
            newList.push(lastElem);
        }

        this.homeworkService.formAddedTests = newList;
        this.syncHomework();
    }

    /**
     * choose exercise button clicked -> open exercise chooser
     */
    chooseExercise(): void {
        this.chooserOpened = true;
        const current = this.pageService.getCurrentPages();

        if (current.length > 0) {
            this.changePage(current[0]);
        } else {
            this.changePage(this.pages[0]);
        }

        if (this.page.index < this.applicationStateService.bookConfig.firstPage) {
            this.changePage(this.pageService.getPageByIndex(this.applicationStateService.bookConfig.firstPage));
        }
    }

    /**
     * close exercise chooser
     */
    back(): void {
        this.chooserOpened = false;
    }

    /**
     * turn to next page
     */
    right(): void {
        let index = this.actIndex();

        if (this.page.index < this.applicationStateService.bookConfig.firstPage) {
            index = this.pages.findIndex(value => {
                return value.index === this.applicationStateService.bookConfig.firstPage;
            }) - 1;
        }

        if (this.isRightPossible()) {
            this.changePage(this.pages[++index]);
        }
    }

    /**
     * is it possible to turn to next page
     *
     * @return {boolean}
     */
    isRightPossible(): boolean {
        return this.actIndex() < this.pages.length - 1;
    }

    /**
     * turn to previous page
     */
    left(): void {
        if (this.isLeftPossible()) {
            let index = this.actIndex();
            this.changePage(this.pages[--index]);
        }
    }

    /**
     * is it possible to turn to previous page
     *
     * @return {boolean}
     */
    isLeftPossible(): boolean {
        if (this.page.index <= this.applicationStateService.bookConfig.firstPage) {
            return false;
        }

        return this.actIndex() > 0;
    }

    /**
     * get actual page index
     *
     * @return {number}
     */
    actIndex(): number {
        return this.pages.findIndex(value => {
            return value.id === this.page.id;
        });
    }

    /**
     * get actual page description
     *
     * @return {string}
     */
    getPage(): string {
        return `Seite ${this.pageService.getRealPageNumber(this.page.index)}`;
    }

    /**
     * change actual page
     *
     * @param {Page} page
     */
    changePage(page: Page): void {
        this.page = page;

        const all = this.quickLinkService.getAllQuickLinksOfPage(this.page.id);
        this.quickLinks = all.genericQuickLinks;
        this.quickLinks = this.quickLinks.filter(value => {
            return this.quickLinkService.isBigEnough(value.position) && this.homeworkService.isQuickLinkExercise(value);
        });
    }

    /**
     * is this quicklink already added to homework
     *
     * @param quickLink
     *
     * @returns {boolean}
     */
    added(quickLink: QuickLink): boolean {
        return this.homeworkService.containsActualHomework(this.quickLinkService.convertQuickLink(quickLink.link, true));
    }

    /**
     * extract exercise code from link text
     *
     * @param quickLink
     *
     * @returns {string}
     */
    linkText(quickLink: QuickLink): string {
        const ex = new SearchResult();
        ex.text = quickLink.link;
        // ex.pageId = this.page.id;
        const result = this.homeworkService.convertExercise(ex);

        return `${result}`;
    }

    /**
     * add or remove link from exercise
     *
     * @param $event
     * @param quickLink
     */
    doAction($event, quickLink: QuickLink): void {
        if (this.added(quickLink)) {
            this.homeworkService.removeExercise(this.quickLinkService.convertQuickLink(quickLink.link, true));
        } else {

            const searchResult = new SearchResult();
            searchResult.text = quickLink.slug;
            this.homeworkService.addExercise(quickLink.link, quickLink.pageId);
        }
        this.communicationService.announceHomeworkChange();
    }

    /**
     * counter for chosen exercises
     *
     * @return {number}
     */
    exerciseCounter(): number {
        return this.homeworkService.formAddedTests.length;
    }

    /**
     * counter for chosen exercises on actual page
     *
     * @return {number}
     */
    exercisePageCounter(): number {
        return this.homeworkService.formAddedTests.filter(value => {
            return value.pageId && this.page.id === value.pageId;
        }).length;
    }

    /**
     * event handler for enter on page input
     *
     * @param event
     */
    keyUpPage(event): void {
        if (KeyValues.getKey(event) === 'Enter' && this.pageNumber !== '') {
            this.enterPage();
        }
    }

    /**
     * change page by inserting page number in input field
     */
    enterPage(): void {
        this.pageNumber = this.pageNumber.replace(/^\D+/g, '');

        const num = parseInt(this.pageNumber);

        const page = this.pages.find(value => {
            return this.pageService.getRealPageNumber(value.index) === num;
        });

        if (page) {
            this.changePage(page);

            return;
        }

        if (num > 0) {
            this.changePage(this.pages[this.pages.length - 1]);
        } else {
            this.changePage(this.pages[0]);
            this.right();
        }
    }

    isValidEmail(email): boolean {
        // eslint-disable-next-line max-len
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        return regex.test(email);
    }

    validateEmail(): void {
        this.validEmail = this.isValidEmail(this.email);
    }

    checkErrors(): void {
        if (!this.isValidEmail(this.email)) {
            this.errorMessages.push('Bitte gib eine gültige E-Mail-Adresse an.');
        }

        if (!this.homeworkService.formAddedTests || this.homeworkService.formAddedTests.length === 0) {
            this.errorMessages.push('Keine Aufgabe vorhanden.');
        }
    }

    clearErrors(): void {
        this.errorMessages = [];
    }

    openDisabledModal(): void {
        this.modalService.openDisabledHSMModal();
    }

    onBookChanged(book): void {
        this.selectedBook = book;

        // if (this.exercise && this.exercise.length >= 3) {
        if (this.exercise && this.exercise.length >= 1) {
            $('#homeworkNumber').focus();
            this.focusDropdown = true;
            this.searchQuery();
        }
    }

    getQuickLinksFromSearchQuery(queryResult: Array<QuickLinkSearchResult>): Array<string> {
        return _.flatten(queryResult.map(ele => queryResult.map(ele => ele.quicklinksExtended
            .map(ql => (ele.bookWrapper.prefix + ql.slug).toLowerCase()))));
    }

    /**
     * close homework editor / deactivate edit mode (and save homework)
     */
    save(openModal?: boolean): void {
        if (this.editHomework) {
            this.loading = true;
            this.crudService.logDirect(TechnicalFeature.TestCreation);

            this.homeworkService.editInBackend(this.editHomework, this.homeworkService.formAddedTests.map((ele => ele.text)))
                .then((updatedTest) => {
                    this.homeworkService.tests = this.homeworkService.tests.map(test => {
                        if (test.id === updatedTest.id) {
                            return Object.assign(new HomeworkLink(), updatedTest);
                        } else {
                            return test;
                        }
                    });
                    this.deleteEditHomework();
                    this.loading = false;
                    this.homeworkService.testGeneratorOpened = false;
                }).catch(() => {
                this.deleteEditHomework();
                this.clearErrors();
                this.errorMessages.push('Die Aufgabe konnte leider nicht erstellt werden. Versuche es bitte später erneut.');
                this.loading = false;
                this.homeworkService.testGeneratorOpened = false;
            });
        } else {
            this.createTest(openModal);
        }
    }

    /**
     * close homework editor / deactivate edit mode (and save homework)
     */
    createTest(openModal?: boolean): void {

        if (!this.isValidEmail(this.email)) {
            return;
        }

        if (this.loading) {
            return;
        }

        this.checkErrors();
        this.syncHomework();
        this.loading = true;

        let notifyText = `${this.notifySchoolClassText} - `;
        if (this.notifySchoolClassText === undefined ||
            this.notifySchoolClassText === '' ||
            this.notifySchoolClassText === this.title
        ) {
            notifyText = '';
        }

        this.homeworkService.saveHomework(true)
            .then(saveHomework => {
                if (this.notifySchoolClass && this.schoolClass > 0) {
                    this.schoolClassService.notifyClass(
                        this.schoolClass,
                        saveHomework.title,
                        `${notifyText}https://${this.applicationStateService.platformDomain}/${saveHomework.link}`
                    );
                }
                this.deleteEditHomework();
                this.loading = false;
                this.clearErrors();
                this.communicationService.clearHomework();
                if (openModal) {
                    setTimeout(() => {
                        const homeworkFactory = new HomeworkFactory();
                        const homework = homeworkFactory.buildFromObject(saveHomework);
                        homework.bypassCheck = true;
                        this.modalService.openModal(homework);
                        this.title = undefined;
                        this.cooldownPhase = null;
                        this.initFields();
                    }, 300);

                }
                this.homeworkService.testGeneratorOpened = false;
            })
            .catch(err => {
                console.log(err);
                this.deleteEditHomework();
                this.loading = false;
                this.clearErrors();
                this.errorMessages.push('Die Aufgabe konnte leider nicht erstellt werden. Versuche es bitte später erneut.');
            });

        this.crudService.logDirect(TechnicalFeature.TestCreation);
    }

    private filterBooks(result): Array<BookWrapper> {
        return result.filter((bookWrapper: BookWrapper) => {
            const bookDifferent = bookWrapper.prefix !== this.applicationStateService.bookConfig.quickLinkPrefix;

            return bookDifferent && (
                !this.selectedBook.schoolLevel || this.selectedBook.schoolLevel.some(num => bookWrapper.schoolLevel.includes(num))
            );
        });
    }

    private fetchRegularQuicklinks(): void {
        this.quickLinkService.findQuickLinksCancelable(this.exercise, this.selectedBook.prefix, false).then(value => {
            if (!this.hsmLinks.length) {
                this.hsmLinks = [new HsmLinkSearchResult()];
                this.hsmLinks[0].bookWrapper = value.data[0].bookWrapper;
            }
            this.hsmLinks[0].hsmLinks.push(
                ...value.data[0].quicklinksExtended.map(
                    (item) => ({ text: item.prefix + item.slug, active: item.active })
                )
            );
        }).catch();
    }

    private addAmLinks(value: Assignment): void {
        if (!value.feature.test) {
            this.errorMessages.push(`Der Test kann nicht aus der Übung ${value.meta.quicklink} erstellt werden.`);

            setTimeout(() => {
                this.errorMessages = [];
            }, 5000);

            return;
        }
        // Lowercase because it should work for e.g. 'GM1B1' and 'gm1b1'
        const quicklinks = value.feature.test;
        for (const key in quicklinks) {
            for (const result of quicklinks[key]) {
                this.clearErrors();
                if (_.filter(this.homeworkService.formAddedTests, ele => {
                    if (typeof ele !== 'string') {
                        return ele.text === result;
                    }

                    return false;
                }).length > 0) {
                    this.errorMessages.push('Diese Aufgabe wurde bereits hinzugefügt.');

                    return;
                }
                this.addQuicklink(result, value, key);
            }
        }
    }

    private addQuicklink(result: any, value: Assignment, teaser?: string) {
        const searchResult = new SearchResult();
        searchResult.text = result;
        if (teaser) {
            searchResult.teaser = teaser;
        }
        searchResult.pageId = this.pageService.getPageIdByIndex(value.meta.page as number);
        this.homeworkService.formAddedTests.push(searchResult);
        this.syncHomework();
        this.isEdited = true;

        this.exercise = '';
        this.hsmLinks = [];
    }

    /**
     * store actual added exercises globally in homework service
     */
    private syncHomework(): void {
        const homework = new HomeworkLink();
        homework.exercises = this.homeworkService.formAddedTests;
        homework.title = this.title;
        homework.email = this.email;
        homework.cooldownPhase = this.cooldownPhase;
        this.homeworkService.syncHomework(homework);
    }

    private initFields(): void {
        if (this.email) {
            this.validateEmail();
        } else {

            if (this.isValidEmail(this.applicationStateService.userData.email)) {
                this.email = this.applicationStateService.userData.email;
                this.validateEmail();
            }
        }

        const today = new Date();
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' } as const;
        const currentDateAsString = today.toLocaleDateString('de-DE', options);

        if (!this.title) {
            this.title = `Test vom ${currentDateAsString}`;
        }
        this.cooldownPhase = (this.editHomework) ? Number(this.editHomework.cooldownPhase) : 1440;
    }

    private deleteEditHomework(): void {
        if (!this.editHomework) {
            return;
        }

        this.editHomework = null;
    }
}
